import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane as PaperPlaneReg } from "@fortawesome/pro-regular-svg-icons";
import { faPaperPlane as PaperPlaneSolid } from "@fortawesome/pro-solid-svg-icons";
import { Badge, Label, TextArea } from "../../../common";
import { advancedTaskHelper } from "../../../../helpers";

interface AddUserTaskCommentFormProps {
  unsavedNewCommentTextValue: string;
  setUnsavedNewCommentTextValue: (value: string) => void;
  clientColor: string;
  onSubmit(
    comment: string,
    successCallback: () => void,
    errorCallback: () => void,
  ): void;
  /** A ref for calling methods on the textarea element, e.g. to focus it */
  inputRef?: React.RefObject<HTMLTextAreaElement> | undefined;
  setFormIsDirty(): void;
  showAddCommentInFormLikeStyle?: boolean;
  taskTypeName: string;
  subjectUserName?: string | null;
}

export const newCommentFieldElementId = "new-comment-input";

function AddUserTaskCommentForm({
  unsavedNewCommentTextValue,
  setUnsavedNewCommentTextValue,
  clientColor,
  onSubmit,
  inputRef = undefined,
  setFormIsDirty,
  showAddCommentInFormLikeStyle = false,
  taskTypeName,
  subjectUserName,
}: AddUserTaskCommentFormProps) {
  const { t } = useTranslation();

  // State
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [erroredWhenSaving, setErroredWhenSaving] = useState<boolean>(false);
  const [isOverSendIcon, setIsOverSendIcon] = useState(false);

  // Events
  const handleSubmitClick = () => {
    const trimmedComment = unsavedNewCommentTextValue?.trim();
    if (trimmedComment && trimmedComment.length > 0) {
      // Submit the comment and update the list of comments
      const successCallback = () => {
        setIsSaving(false);
        setErroredWhenSaving(false);
        // Reset the form
        setUnsavedNewCommentTextValue("");
      };

      const errorCallback = () => {
        setIsSaving(false);
        setErroredWhenSaving(true);
      };

      // Reset variables
      setErroredWhenSaving(false);

      // Show the saving spinner
      setIsSaving(true);

      onSubmit(trimmedComment, successCallback, errorCallback);
    }
  };

  const onTextValueChange = (text: string) => {
    setUnsavedNewCommentTextValue(text);
    setFormIsDirty();
  };

  return (
    <div className="border border-gray-300 rounded-lg py-2 mb-2">
      {showAddCommentInFormLikeStyle && subjectUserName && (
        <div className="pl-2 md:pl-0">
          <Label
            text={t("TaskType.EnforceComments.AddComment.QuestionText", {
              subjectEmployeeName: subjectUserName,
              taskType: advancedTaskHelper.ToLowerCase(taskTypeName),
            })}
          />
          <Badge
            text="Required"
            textColourClassName="text-gray-600"
            marginClassName="ml-2"
          />
        </div>
      )}
      <div className="flex">
        <div className="pl-2 grow min-w-0 md:pl-0">
          <TextArea
            inputId={newCommentFieldElementId}
            placeholder={t(
              "Pages.CollaborativeDocument.Controls.NewCommentPlaceholder",
            )}
            value={unsavedNewCommentTextValue}
            onChange={onTextValueChange}
            className="block w-full border-0 bg-gray-100 rounded-lg text-sm"
            minRows={1}
            maxRows={4}
            inputRef={inputRef}
            showValidationErrors={false}
          />
        </div>
        <div className="w-2/12 md:w-1/12">
          <div className="text-center pt-2">
            <FontAwesomeIcon
              title="Add comment"
              className="cursor-pointer"
              onMouseEnter={() => setIsOverSendIcon(true)}
              onMouseLeave={() => setIsOverSendIcon(false)}
              onClick={handleSubmitClick}
              color={clientColor}
              icon={!isOverSendIcon ? PaperPlaneReg : PaperPlaneSolid}
            />
          </div>
        </div>
      </div>
      {erroredWhenSaving && (
        <small className="pl-2">
          <span className="text-red-500 mr-2">
            {t("Pages.CollaborativeDocument.Common.AddCommentErrored")}
          </span>
          <button className="underline" onClick={handleSubmitClick}>
            {t("Common.TryAgain")}
          </button>
        </small>
      )}
    </div>
  );
}

export default AddUserTaskCommentForm;
